import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import apiClient from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { loadRazorpayCheckout } from '../../utils/razorpay';
import { formatCurrency, formatDate } from '../../utils/format';

const CONTACT_EMAIL = 'kirathaent@gmail.com';

function Billing() {
  const { t } = useTranslation(['billing', 'common']);
  const navigate = useNavigate();
  const { email, fullName } = useAuth();
  const { status, refetch } = useSubscription();

  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('MONTHLY');
  const [payingPlan, setPayingPlan] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPricingError(false);
    apiClient.get('/api/public/pricing')
      .then((res) => setPricing(res.data))
      .catch(() => setPricingError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const planByKey = (key) => pricing?.plans?.find((p) => p.key === key);

  const handlePay = async (planKey) => {
    if (planKey === 'ENTERPRISE') {
      const subject = encodeURIComponent(t('enterprise.ctaSubject'));
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}`;
      return;
    }

    setPayingPlan(planKey);
    try {
      const scriptOk = await loadRazorpayCheckout();
      if (!scriptOk) {
        setToast({ type: 'error', message: t('toast.checkoutLoadFailed') });
        setPayingPlan(null);
        return;
      }

      const { data: order } = await apiClient.post('/api/payments/create-order', {
        plan: planKey,
        period,
      });

      const options = {
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency,
        order_id: order.orderId,
        name: 'BentoPing',
        description: t('checkout.description', {
          plan: t(`plans.${planKey.toLowerCase()}.name`),
          period: t(`periods.${period.toLowerCase()}`),
        }),
        prefill: { name: fullName || undefined, email: email || undefined },
        theme: { color: '#f97316' },
        modal: {
          ondismiss: () => setPayingPlan(null),
        },
        handler: async (response) => {
          try {
            await apiClient.post('/api/payments/verify', {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            await refetch();
            setToast({ type: 'success', message: t('toast.paymentSuccess') });
          } catch (err) {
            setToast({ type: 'error', message: err.response?.data?.message || t('toast.verifyFailed') });
          } finally {
            setPayingPlan(null);
          }
        },
      };

      const checkout = new window.Razorpay(options);
      checkout.on('payment.failed', () => {
        setToast({ type: 'error', message: t('toast.paymentFailed') });
        setPayingPlan(null);
      });
      checkout.open();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || t('toast.orderFailed') });
      setPayingPlan(null);
    }
  };

  const subscribedPlan = status?.subscribedPlan;
  const expiresAt = status?.subscriptionExpiresAt;
  const daysRemaining = status?.daysRemaining;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] flex items-center gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg max-w-xs ${
            toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-current opacity-60 hover:opacity-100 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={[
          <button
            key="back"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
          >
            <ChevronLeft size={15} />
            {t('back')}
          </button>,
        ]}
      />

      <div className="flex-1 px-6 py-6 max-w-4xl mx-auto w-full space-y-6">
        {/* Current plan */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('currentPlan.title')}</h2>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-orange-50 border border-orange-200 px-3 py-1 text-sm font-semibold text-orange-700">
              {subscribedPlan ? t(`plans.${subscribedPlan.toLowerCase()}.name`) : t('currentPlan.none')}
            </span>
            {expiresAt && (
              <span className="text-sm text-gray-600">
                {daysRemaining != null
                  ? t('currentPlan.expiresInDays', { date: formatDate(expiresAt), days: daysRemaining })
                  : t('currentPlan.expiresOn', { date: formatDate(expiresAt) })}
              </span>
            )}
          </div>
        </div>

        {/* Period toggle */}
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setPeriod('MONTHLY')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                period === 'MONTHLY' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('periods.monthly')}
            </button>
            <button
              onClick={() => setPeriod('ANNUAL')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                period === 'ANNUAL' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('periods.annual')}
            </button>
          </div>
          {period === 'ANNUAL' && <span className="text-xs text-green-700">{t('periods.annualHint')}</span>}
        </div>

        {loading && <p className="text-center text-sm text-gray-500 py-12">{t('common:loading')}</p>}

        {!loading && pricingError && (
          <p className="text-center text-sm text-red-600 py-12">{t('loadError')}</p>
        )}

        {!loading && !pricingError && pricing && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {['STARTER', 'GROWTH', 'ENTERPRISE'].map((planKey) => {
              const plan = planByKey(planKey);
              const isEnterprise = planKey === 'ENTERPRISE';
              const isCurrent = subscribedPlan === planKey;
              const price = plan ? (period === 'ANNUAL' ? plan.annualPriceInr : plan.priceInr) : null;
              const isPaying = payingPlan === planKey;
              // Disable other plans' buttons while one checkout is in flight — never disable
              // Enterprise, whose "button" is just a mailto and has no async state.
              const disablePay = !isEnterprise && payingPlan !== null && !isPaying;

              return (
                <div
                  key={planKey}
                  className={`relative flex flex-col rounded-xl border-2 p-6 ${
                    isCurrent ? 'border-orange-500 bg-orange-50/40' : 'border-gray-200 bg-white'
                  }`}
                >
                  {isCurrent && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-0.5 text-xs font-semibold text-green-700 border border-green-200">
                      <CheckCircle2 size={12} />
                      {t('currentPlan.badge')}
                    </span>
                  )}

                  <h3 className="text-lg font-bold text-gray-900 text-center mt-2">
                    {t(`plans.${planKey.toLowerCase()}.name`)}
                  </h3>

                  <p className="text-center mt-2">
                    {isEnterprise ? (
                      <span className="text-2xl font-extrabold text-gray-900">{t('enterprise.priceLabel')}</span>
                    ) : (
                      <>
                        <span className="text-2xl font-extrabold text-gray-900">
                          {price != null ? formatCurrency(price) : '—'}
                        </span>
                        <span className="text-sm text-gray-500">
                          {period === 'ANNUAL' ? t('periods.perYear') : t('periods.perMonth')}
                        </span>
                      </>
                    )}
                  </p>

                  {plan?.staffCap != null && (
                    <p className="text-center text-sm text-gray-600 mt-1">
                      {t('staffCap', { count: plan.staffCap })}
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={disablePay || isPaying}
                    onClick={() => handlePay(planKey)}
                    className={`mt-6 w-full rounded-xl border-2 px-6 py-3 text-base font-bold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                      isEnterprise
                        ? 'border-gray-200 bg-white text-gray-800 hover:border-orange-400 hover:text-orange-700'
                        : 'border-orange-500 bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md shadow-orange-200 hover:from-orange-600 hover:to-amber-700'
                    }`}
                  >
                    {isEnterprise
                      ? t('enterprise.cta')
                      : isPaying
                      ? t('paying')
                      : t('pay', { price: price != null ? formatCurrency(price) : '' })}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Billing;
